import React from "react";
import {
  Activity,
  BarChart3,
  BookOpen,
  CalendarDays,
  IndianRupee,
  MessageCircle,
  Minus,
  Percent,
  ShoppingBag,
  ShoppingCart,
  Ticket,
  TrendingDown,
  TrendingUp,
  Trophy,
  Users,
} from "lucide-react";
import "../CustomTheme/customTheme.css";
import "./CustomStatCard.css";

/**
 * CustomStatCard — KPI / summary card.
 *
 * Props:
 *  - title       e.g. "Total Revenue"
 *  - value       already formatted, e.g. "₹4,99,952"
 *  - subtitle    small grey line under the value, e.g. "Revenue ÷ Cost"
 *  - icon        a lucide icon element, e.g. <CreditCard size={18} />; left out → picked from the title
 *                (pass icon={false} for no icon)
 *  - tone        "primary" (default) | "success" | "danger" | "warning" | "info" | "neutral"
 *  - info        text for the "?" tooltip
 *  - trend       { value: 12.5, label: "vs last week", invert?: bool }
 *                positive = green ↑, negative = red ↓ (invert flips for "lower is better")
 *  - loading     shows a skeleton instead of the value
 *  - onClick     makes the whole card clickable
 *  - active      highlights the card (e.g. the selected filter card)
 *  - action      element shown at the right of the header (not for clickable cards)
 *  - colClass, className, style
 */
// first match wins; every card gets an icon even when the page passes none
const AUTO_ICONS = [
  [/conversion|ratio|rate|%/i, Percent],
  [/message|whatsapp|sent|delivered|read|failed/i, MessageCircle],
  [/coupon/i, Ticket],
  [/cart/i, ShoppingCart],
  [/order/i, ShoppingBag],
  [/revenue|amount|payment|cashback|price|fee|value|refund|₹/i, IndianRupee],
  [/best|top|rank/i, Trophy],
  [/day|date|month|week|year/i, CalendarDays],
  [/course|batch|book|test series|exam/i, BookOpen],
  [/activity|session|visitor/i, Activity],
  [/user|student|lead|registration|counsellor|influencer|member|people|expired|expiry/i, Users],
];
export const autoStatIcon = (title) => {
  const text = typeof title === "string" ? title : "";
  const Icon = (AUTO_ICONS.find(([re]) => re.test(text)) || [null, BarChart3])[1];
  return <Icon size={18} />;
};

const CustomStatCard = ({
  title,
  value,
  subtitle,
  icon,
  tone = "primary",
  info,
  trend,
  loading = false,
  onClick,
  active = false,
  action,
  colClass,
  className,
  style,
}) => {
  const trendValue = Number(trend?.value);
  const hasTrend = trend && !Number.isNaN(trendValue);
  const isUp = trendValue > 0;
  const isGood = trend?.invert ? trendValue < 0 : trendValue > 0;
  const TrendIcon = trendValue === 0 ? Minus : isUp ? TrendingUp : TrendingDown;
  const trendClass = trendValue === 0 ? "csc-trend-flat" : isGood ? "csc-trend-good" : "csc-trend-bad";

  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "article";

  const card = (
    <Tag
      type={clickable ? "button" : undefined}
      className={`csc-card csc-tone-${tone}${clickable ? " csc-clickable" : ""}${
        active ? " csc-active" : ""
      }${className ? ` ${className}` : ""}`}
      style={style}
      onClick={onClick}
      aria-pressed={clickable ? active : undefined}
    >
      <div className="csc-head">
        {icon !== false && <span className="csc-icon">{icon || autoStatIcon(title)}</span>}
        <span className="csc-title" title={title}>
          {title}
        </span>
        {info && (
          <span className="csc-info" title={info} aria-label={info}>
            ?
          </span>
        )}
        {action && !clickable && <span className="csc-action">{action}</span>}
      </div>

      {loading ? (
        <span className="csc-skeleton" aria-label="Loading" />
      ) : (
        <strong className="csc-value">{value ?? "—"}</strong>
      )}

      {subtitle && <small className="csc-sub csc-subtitle">{subtitle}</small>}

      {hasTrend && !loading && (
        <div className="csc-foot">
          <span className={`csc-trend ${trendClass}`}>
            <TrendIcon size={13} />
            {Math.abs(trendValue)}%
          </span>
          {trend.label && <small className="csc-sub">{trend.label}</small>}
        </div>
      )}
    </Tag>
  );

  return colClass ? <div className={colClass}>{card}</div> : card;
};

export default CustomStatCard;
